import React, { useState, useRef, useEffect } from "react";
import {
  Stage,
  Layer,
  Rect,
  Circle,
  Line,
  Text,
  Group,
  Arrow,
} from "react-konva";

const initialRooms = [
  {
    id: "room1",
    name: "Reception",
    x: 50,
    y: 50,
    width: 200,
    height: 150,
    color: "#e3f2fd",
    connections: ["room2", "corridor1"],
    type: "office",
  },
  {
    id: "room2",
    name: "Office A",
    x: 300,
    y: 50,
    width: 150,
    height: 150,
    color: "#f3e5f5",
    connections: ["room1", "corridor1", "room5"],
    type: "office",
  },
  {
    id: "corridor1",
    name: "Main Corridor",
    x: 200,
    y: 220,
    width: 450,
    height: 80,
    color: "#fff8e1",
    connections: ["room1", "room2", "room3", "room4", "corridor2"],
    type: "corridor",
  },
  {
    id: "room3",
    name: "Conference Room",
    x: 50,
    y: 320,
    width: 180,
    height: 140,
    color: "#e8f5e8",
    connections: ["corridor1"],
    type: "meeting",
  },
  {
    id: "room4",
    name: "Office B",
    x: 480,
    y: 320,
    width: 170,
    height: 140,
    color: "#ffebee",
    connections: ["corridor1", "room6"],
    type: "office",
  },
  {
    id: "corridor2",
    name: "East Wing",
    x: 670,
    y: 150,
    width: 80,
    height: 300,
    color: "#fff8e1",
    connections: ["corridor1", "room5", "room6"],
    type: "corridor",
  },
  {
    id: "room5",
    name: "Cafeteria",
    x: 470,
    y: 50,
    width: 180,
    height: 150,
    color: "#ffe0b2",
    connections: ["room2", "corridor2"],
    type: "amenity",
  },
  {
    id: "room6",
    name: "Storage",
    x: 670,
    y: 470,
    width: 150,
    height: 100,
    color: "#cfd8dc",
    connections: ["room4", "corridor2"],
    type: "storage",
  },
];

const FloorPlanMap = () => {
  const [rooms, setRooms] = useState(initialRooms);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [startRoom, setStartRoom] = useState(null);
  const [endRoom, setEndRoom] = useState(null);
  const [path, setPath] = useState([]);
  const [showAllConnections, setShowAllConnections] = useState(false);
  const [hoveredRoom, setHoveredRoom] = useState(null);
  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [isDraggingRoom, setIsDraggingRoom] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const stageRef = useRef();
  const lineRef = useRef(null);

  const getRoomCenter = (room) => ({
    x: room.x + room.width / 2,
    y: room.y + room.height / 2,
  });

  const getConnectionPoints = (roomA, roomB) => {
    const centerA = getRoomCenter(roomA);
    const centerB = getRoomCenter(roomB);
    const dx = centerB.x - centerA.x;
    const dy = centerB.y - centerA.y;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    let startX, startY, endX, endY;

    if (absDx > absDy) {
      // horizontal connection
      const avgY = (centerA.y + centerB.y) / 2;
      if (dx > 0) {
        // A right to B left
        startX = roomA.x + roomA.width;
        startY = Math.max(roomA.y, Math.min(roomA.y + roomA.height, avgY));
        endX = roomB.x;
        endY = Math.max(roomB.y, Math.min(roomB.y + roomB.height, avgY));
      } else {
        // A left to B right
        startX = roomA.x;
        startY = Math.max(roomA.y, Math.min(roomA.y + roomA.height, avgY));
        endX = roomB.x + roomB.width;
        endY = Math.max(roomB.y, Math.min(roomB.y + roomB.height, avgY));
      }
    } else {
      // vertical connection
      const avgX = (centerA.x + centerB.x) / 2;
      if (dy > 0) {
        // A bottom to B top
        startY = roomA.y + roomA.height;
        startX = Math.max(roomA.x, Math.min(roomA.x + roomA.width, avgX));
        endY = roomB.y;
        endX = Math.max(roomB.x, Math.min(roomB.x + roomB.width, avgX));
      } else {
        // A top to B bottom
        startY = roomA.y;
        startX = Math.max(roomA.x, Math.min(roomA.x + roomA.width, avgX));
        endY = roomB.y + roomB.height;
        endX = Math.max(roomB.x, Math.min(roomB.x + roomB.width, avgX));
      }
    }

    return { startX, startY, endX, endY };
  };

  const calculateBoundaryPathPoints = (pathIds) => {
    const points = [];
    if (pathIds.length < 1) return points;

    // Start at center of first room
    const firstRoom = rooms.find((r) => r.id === pathIds[0]);
    if (firstRoom) {
      const center = getRoomCenter(firstRoom);
      points.push(center.x, center.y);
    }

    // Add connection points for each segment
    for (let i = 0; i < pathIds.length - 1; i++) {
      const roomAId = pathIds[i];
      const roomBId = pathIds[i + 1];
      const roomA = rooms.find((r) => r.id === roomAId);
      const roomB = rooms.find((r) => r.id === roomBId);
      if (!roomA || !roomB) continue;

      const conn = getConnectionPoints(roomA, roomB);
      points.push(conn.startX, conn.startY, conn.endX, conn.endY);
    }

    // End at center of last room
    const lastRoom = rooms.find((r) => r.id === pathIds[pathIds.length - 1]);
    if (lastRoom) {
      const center = getRoomCenter(lastRoom);
      points.push(center.x, center.y);
    }

    return points;
  };

  // Set path line points
  useEffect(() => {
    const line = lineRef.current;
    if (!line) return;

    if (path.length <= 1) {
      line.points([]);
      return;
    }

    const points = calculateBoundaryPathPoints(path);
    line.points(points);
  }, [path, rooms]);

  const findPath = (startId, endId) => {
    const graph = {};
    rooms.forEach((room) => {
      graph[room.id] = room.connections.map((connId) => {
        const targetRoom = rooms.find((r) => r.id === connId);
        const distance = targetRoom ? heuristic(room.id, connId) : Infinity;
        return {
          id: connId,
          weight: distance / 100,
        };
      });
    });

    const openSet = new Set([startId]);
    const cameFrom = {};
    const gScore = { [startId]: 0 };
    const fScore = { [startId]: heuristic(startId, endId) };

    while (openSet.size > 0) {
      let current = null;
      let lowestFScore = Infinity;

      openSet.forEach((node) => {
        if (fScore[node] < lowestFScore) {
          lowestFScore = fScore[node];
          current = node;
        }
      });

      if (current === endId) {
        const totalPath = [current];
        while (cameFrom[current]) {
          current = cameFrom[current];
          totalPath.unshift(current);
        }
        return totalPath;
      }

      openSet.delete(current);

      graph[current]?.forEach((neighbor) => {
        const tentativeGScore = gScore[current] + neighbor.weight;

        if (!gScore[neighbor.id] || tentativeGScore < gScore[neighbor.id]) {
          cameFrom[neighbor.id] = current;
          gScore[neighbor.id] = tentativeGScore;
          fScore[neighbor.id] = tentativeGScore + heuristic(neighbor.id, endId);

          if (!openSet.has(neighbor.id)) {
            openSet.add(neighbor.id);
          }
        }
      });
    }

    return [];
  };

  const heuristic = (roomIdA, roomIdB) => {
    const roomA = rooms.find((r) => r.id === roomIdA);
    const roomB = rooms.find((r) => r.id === roomIdB);
    if (!roomA || !roomB) return Infinity;

    const centerA = getRoomCenter(roomA);
    const centerB = getRoomCenter(roomB);

    return Math.sqrt(
      Math.pow(centerB.x - centerA.x, 2) + Math.pow(centerB.y - centerA.y, 2)
    );
  };

  const handleRoomClick = (room) => {
    if (editMode) return;

    setSelectedRoom(room);

    if (!startRoom) {
      setStartRoom(room);
    } else if (!endRoom && room.id !== startRoom.id) {
      setEndRoom(room);
      const pathIds = findPath(startRoom.id, room.id);
      setPath(pathIds);
    } else {
      setStartRoom(room);
      setEndRoom(null);
      setPath([]);
    }
  };

  const handleRoomDragEnd = (e, roomId) => {
    if (!editMode) return;

    const updatedRooms = rooms.map((room) => {
      if (room.id === roomId) {
        return {
          ...room,
          x: e.target.x(),
          y: e.target.y(),
        };
      }
      return room;
    });
    setRooms(updatedRooms);
    setIsDraggingRoom(false);
  };

  const renderConnections = () => {
    if (!showAllConnections) return null;

    const connections = [];
    const drawn = new Set();

    rooms.forEach((room) => {
      room.connections.forEach((connId) => {
        const key = [room.id, connId].sort().join("-");
        if (drawn.has(key)) return;
        drawn.add(key);

        const targetRoom = rooms.find((r) => r.id === connId);
        if (!targetRoom) return;

        const conn = getConnectionPoints(room, targetRoom);

        connections.push(
          <Line
            key={key}
            points={[conn.startX, conn.startY, conn.endX, conn.endY]}
            stroke="#90caf9"
            strokeWidth={2}
            dash={[8, 4]}
            opacity={0.5}
          />
        );
      });
    });

    return connections;
  };

  const resetPath = () => {
    setStartRoom(null);
    setEndRoom(null);
    setPath([]);
    setSelectedRoom(null);
  };

  const handleZoom = (delta) => {
    const newScale = Math.max(0.5, Math.min(2, scale + delta));
    setScale(newScale);
  };

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    const stage = e.target.getStage();
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    const bounded = Math.max(0.5, Math.min(2, newScale));

    setScale(bounded);
    setStagePos({
      x: pointer.x - mousePointTo.x * bounded,
      y: pointer.y - mousePointTo.y * bounded,
    });
  };

  const getPathDistance = () => {
    if (path.length < 2) return 0;

    let distance = 0;
    for (let i = 0; i < path.length - 1; i++) {
      const roomA = rooms.find((r) => r.id === path[i]);
      const roomB = rooms.find((r) => r.id === path[i + 1]);
      if (roomA && roomB) {
        distance += heuristic(roomA.id, roomB.id);
      }
    }
    return Math.round(distance);
  };

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>
        <h2 style={{ marginBottom: "20px", color: "#333" }}>
          🗺️ Interactive Floor Plan Navigator
        </h2>

        <div
          style={{
            backgroundColor: "white",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "20px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              marginBottom: "15px",
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => setShowAllConnections(!showAllConnections)}
              style={{
                padding: "10px 20px",
                backgroundColor: showAllConnections ? "#2196f3" : "#fff",
                color: showAllConnections ? "#fff" : "#333",
                border: "1px solid #2196f3",
                borderRadius: "5px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              {showAllConnections ? "👁️ Hide" : "👁️ Show"} Connections
            </button>

            <button
              onClick={() => setEditMode(!editMode)}
              style={{
                padding: "10px 20px",
                backgroundColor: editMode ? "#ff9800" : "#fff",
                color: editMode ? "#fff" : "#333",
                border: "1px solid #ff9800",
                borderRadius: "5px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              {editMode ? "🔒 Lock" : "✏️ Edit"} Mode
            </button>

            <button
              onClick={resetPath}
              style={{
                padding: "10px 20px",
                backgroundColor: "#f44336",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              🔄 Reset Path
            </button>

            <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
              <button
                onClick={() => handleZoom(-0.1)}
                style={{
                  padding: "10px 15px",
                  backgroundColor: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "18px",
                }}
              >
                −
              </button>
              <span style={{ padding: "0 10px", fontWeight: "500" }}>
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={() => handleZoom(0.1)}
                style={{
                  padding: "10px 15px",
                  backgroundColor: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "18px",
                }}
              >
                +
              </button>
            </div>
          </div>

          {startRoom && (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#e8f5e9",
                borderRadius: "5px",
                borderLeft: "4px solid #4caf50",
              }}
            >
              <p style={{ margin: 0, fontSize: "14px" }}>
                <strong>📍 Start:</strong> {startRoom.name}
                {endRoom && (
                  <>
                    <strong style={{ marginLeft: "15px" }}>
                      🎯 Destination:
                    </strong>{" "}
                    {endRoom.name}
                    {path.length > 0 && (
                      <span style={{ marginLeft: "15px", color: "#666" }}>
                        ({path.length} stops • ~{getPathDistance()}px distance)
                      </span>
                    )}
                  </>
                )}
                {!endRoom && (
                  <span style={{ color: "#666", marginLeft: "10px" }}>
                    - Click another room to set destination
                  </span>
                )}
              </p>
            </div>
          )}

          {path.length === 0 && startRoom && endRoom && (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#ffebee",
                borderRadius: "5px",
                marginTop: "10px",
                borderLeft: "4px solid #f44336",
              }}
            >
              <p style={{ margin: 0, color: "#c62828", fontSize: "14px" }}>
                ⚠️ No path found between selected rooms!
              </p>
            </div>
          )}

          {editMode && (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#fff3e0",
                borderRadius: "5px",
                marginTop: "10px",
                borderLeft: "4px solid #ff9800",
              }}
            >
              <p style={{ margin: 0, fontSize: "14px" }}>
                ✏️ <strong>Edit Mode Active:</strong> Drag rooms to reposition
                them
              </p>
            </div>
          )}
        </div>

        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Stage
            width={860}
            height={600}
            ref={stageRef}
            scaleX={scale}
            scaleY={scale}
            x={stagePos.x}
            y={stagePos.y}
            draggable={!editMode && !isDraggingRoom}
            onWheel={handleWheel}
            style={{ cursor: editMode ? "default" : "grab" }}
          >
            <Layer>
              {renderConnections()}

              {rooms.map((room) => (
                <Group
                  key={room.id}
                  x={room.x}
                  y={room.y}
                  draggable={editMode}
                  onDragStart={() => setIsDraggingRoom(true)}
                  onDragEnd={(e) => handleRoomDragEnd(e, room.id)}
                  onMouseEnter={() => !editMode && setHoveredRoom(room)}
                  onMouseLeave={() => setHoveredRoom(null)}
                >
                  <Rect
                    width={room.width}
                    height={room.height}
                    fill={room.color}
                    stroke={
                      selectedRoom?.id === room.id
                        ? "#2196f3"
                        : startRoom?.id === room.id
                        ? "#4caf50"
                        : endRoom?.id === room.id
                        ? "#ff5722"
                        : hoveredRoom?.id === room.id
                        ? "#666"
                        : "#bbb"
                    }
                    strokeWidth={
                      selectedRoom?.id === room.id ||
                      startRoom?.id === room.id ||
                      endRoom?.id === room.id
                        ? 4
                        : hoveredRoom?.id === room.id
                        ? 2
                        : 1
                    }
                    onClick={() => handleRoomClick(room)}
                    onTap={() => handleRoomClick(room)}
                    cornerRadius={8}
                    shadowColor="rgba(0,0,0,0.15)"
                    shadowBlur={8}
                    shadowOffset={{ x: 2, y: 2 }}
                    shadowOpacity={hoveredRoom?.id === room.id ? 0.3 : 0.15}
                  />

                  <Text
                    x={10}
                    y={10}
                    text={room.name}
                    fontSize={16}
                    fontFamily="Arial"
                    fill="#333"
                    fontStyle="bold"
                  />

                  <Text
                    x={10}
                    y={30}
                    text={`${room.width}×${room.height}`}
                    fontSize={11}
                    fontFamily="Arial"
                    fill="#666"
                  />
                </Group>
              ))}

              {path.length > 1 && (
                <Line
                  ref={lineRef}
                  points={[]}
                  stroke="#ff5722"
                  strokeWidth={5}
                  lineCap="round"
                  lineJoin="round"
                  tension={0.2}
                  shadowColor="rgba(255,87,34,0.5)"
                  shadowBlur={10}
                />
              )}

              {path.length > 1 &&
                path.map((roomId, index) => {
                  const room = rooms.find((r) => r.id === roomId);
                  if (!room) return null;

                  const center = getRoomCenter(room);
                  const isStart = index === 0;
                  const isEnd = index === path.length - 1;

                  return (
                    <Group key={`marker-${roomId}-${index}`}>
                      <Circle
                        x={center.x}
                        y={center.y}
                        radius={10}
                        fill={
                          isStart ? "#4caf50" : isEnd ? "#ff5722" : "#2196f3"
                        }
                        stroke="white"
                        strokeWidth={3}
                        shadowColor="rgba(0,0,0,0.3)"
                        shadowBlur={5}
                      />
                      {!isEnd && (
                        <Text
                          x={center.x + 15}
                          y={center.y - 8}
                          text={(index + 1).toString()}
                          fontSize={14}
                          fontFamily="Arial"
                          fill="#2196f3"
                          fontStyle="bold"
                        />
                      )}
                    </Group>
                  );
                })}
            </Layer>
          </Stage>
        </div>

        <div
          style={{
            marginTop: "20px",
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333" }}>
            📖 Instructions
          </h3>
          <ul style={{ lineHeight: "1.8", color: "#666" }}>
            <li>
              <strong>Navigation:</strong> Click a room to set starting point,
              then click another to find path
            </li>
            <li>
              <strong>Colors:</strong> 🟢 Start point • 🔴 End point • 🔵
              Waypoints
            </li>
            <li>
              <strong>Zoom:</strong> Use mouse wheel or +/- buttons to zoom
              in/out
            </li>
            <li>
              <strong>Pan:</strong> Click and drag the canvas to move around
            </li>
            <li>
              <strong>Edit Mode:</strong> Enable to drag and reposition rooms
            </li>
            <li>
              <strong>Connections:</strong> Toggle to see all possible paths
              between rooms
            </li>
          </ul>

          <div
            style={{
              marginTop: "20px",
              padding: "15px",
              backgroundColor: "#f5f5f5",
              borderRadius: "5px",
            }}
          >
            <h4 style={{ margin: "0 0 10px 0", color: "#555" }}>Legend</h4>
            <div
              style={{
                display: "flex",
                gap: "20px",
                flexWrap: "wrap",
                fontSize: "14px",
              }}
            >
              <div>
                <span
                  style={{
                    backgroundColor: "#e3f2fd",
                    padding: "2px 8px",
                    borderRadius: "3px",
                  }}
                >
                  ■
                </span>{" "}
                Office
              </div>
              <div>
                <span
                  style={{
                    backgroundColor: "#fff8e1",
                    padding: "2px 8px",
                    borderRadius: "3px",
                  }}
                >
                  ■
                </span>{" "}
                Corridor
              </div>
              <div>
                <span
                  style={{
                    backgroundColor: "#e8f5e8",
                    padding: "2px 8px",
                    borderRadius: "3px",
                  }}
                >
                  ■
                </span>{" "}
                Meeting Room
              </div>
              <div>
                <span
                  style={{
                    backgroundColor: "#ffe0b2",
                    padding: "2px 8px",
                    borderRadius: "3px",
                  }}
                >
                  ■
                </span>{" "}
                Amenity
              </div>
              <div>
                <span
                  style={{
                    backgroundColor: "#cfd8dc",
                    padding: "2px 8px",
                    borderRadius: "3px",
                  }}
                >
                  ■
                </span>{" "}
                Storage
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorPlanMap;
